import React from 'react';
import { IProjectActionsData } from '../../../redux/project/sliceProjectActions';
import i18n from '../../../localization/i18n';
import SelectedDocumentsDownloadButton from '../../Views/ProjectView/SelectedDocumentsDownloadButton';
import ActionButton from '../ActionButton/ActionButton';
import { devOnClick, docTableActionsCallbacks } from '../DocTable/DocTableActions';
import { IProjectViewData } from '../../../redux/project/sliceProjectViews';
import { SelectedDocumentsMenuButton } from '../../Views/ProjectView/SelectedDocumentsMenuButton';
import { mdiDotsVertical } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { vars } from '../../../Theme';
import { defineActionButtonsAvailability, getToolTip } from '../../../util/DataUtils';
import AppTooltip from '../AppTooltip/AppTooltip';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';

const getAvailableActions = (
    availableSnackbarActions: IAvailableSnackbarAction[],
): IAvailableSnackbarAction[] => {
    if (!availableSnackbarActions) return [] as IAvailableSnackbarAction[];

    if (availableSnackbarActions?.length <= 6) {
        return availableSnackbarActions;
    }
    if (availableSnackbarActions?.length > 6) {
        return [
            ...(availableSnackbarActions ?? []).slice(0, 5),
            {
                name: 'open',
                configurable: false,
                actionGroups: null,
                icon: 'kebabIcon',
                translations: {
                    [i18n.language]: '',
                },
            },
        ];
    }
};
export default ({
    availableSnackbarActionsList,
    replicaId,
    documentIds,
    currentView,
    reportName,
}: ISnackbarActionsProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });
    const pendingActions = useSelector((store: IAppReduxStore) => store.actionHandling.actions);

    return (
        <>
            {getAvailableActions(availableSnackbarActionsList).map(
                ({ name, icon, translations, color, groupActions, extendedConfig }) => {
                    const { maxDocs, minDocs } = extendedConfig ?? { maxDocs: 0, minDocs: 0 };
                    if (groupActions?.length) {
                        return (
                            <SelectedDocumentsMenuButton
                                key={name}
                                actions={groupActions}
                                icon={icon ?? ''}
                                text={translations[i18n.language] ?? name}
                                color={color ?? vars.font}
                                replicaId={replicaId}
                                documentIds={documentIds}
                                currentView={currentView}
                                reportName={reportName}
                            />
                        );
                    }
                    if (name === 'download') {
                        return (
                            <SelectedDocumentsDownloadButton
                                key={name}
                                icon={icon}
                                text={translations[i18n.language] ?? name}
                                maxDocs={maxDocs}
                                minDocs={minDocs}
                            />
                        );
                    } else if (icon === 'kebabIcon') {
                        return (
                            <SelectedDocumentsMenuButton
                                key={'kebab'}
                                actions={availableSnackbarActionsList?.slice(5)}
                                icon={mdiDotsVertical}
                                text={t('more')}
                                replicaId={replicaId}
                                documentIds={documentIds}
                                currentView={currentView}
                                reportName={reportName}
                            />
                        );
                    } else {
                        const isActionButtonDisabled =
                            defineActionButtonsAvailability(documentIds.length, maxDocs, minDocs) ||
                            (pendingActions ?? []).includes(name);
                        return (
                            <AppTooltip
                                key={name}
                                title={
                                    isActionButtonDisabled
                                        ? t(getToolTip(maxDocs, minDocs), { maxDocs, minDocs })
                                        : null
                                }
                                placement='top-start'
                            >
                                <Box>
                                    <ActionButton
                                        key={name}
                                        icon={icon}
                                        text={translations[i18n.language] ?? name}
                                        onClick={() =>
                                            (extendedConfig?.dynamicAction
                                                ? docTableActionsCallbacks.dynamicAction
                                                : docTableActionsCallbacks[name] ?? devOnClick)({
                                                replicaId,
                                                documentIds,
                                                reportName,
                                                currentView,
                                                actionKey: name,
                                            })
                                        }
                                        disabled={isActionButtonDisabled}
                                        color={
                                            isActionButtonDisabled ? vars.disabledGrey : vars.font
                                        }
                                    />
                                </Box>
                            </AppTooltip>
                        );
                    }
                },
            )}
        </>
    );
};

export interface IAvailableSnackbarAction extends IProjectActionsData {
    groupActions?: IProjectActionsData[];
    color?: string;
}

interface ISnackbarActionsProps {
    availableSnackbarActionsList: IAvailableSnackbarAction[];
    replicaId: string;
    documentIds: string[];
    currentView: IProjectViewData;
    reportName: string;
}
